import { useState } from 'react';

import { AsyncBoundary, Drawer, Icon, useToast } from '@kollez/design-system';
import { usePlatformAudit, type AuditRow } from '@/lib/queries';
import { formatIndianDate, formatTime } from '@kollez/shared';

/**
 * /platform/audit (F88c) — direct port of kollez/pages/superadmin/audit.html.
 *   Header + Export · filters (action / actor role) + search
 *   Table: When · Who + IP · Action badge (warning for View-as/Deletion/Score) · What · Tenant
 *   Drawer: dl + integrity note + code preview with hash chain
 */

const ACTIONS = [
  '', 'user.login', 'user.created', 'offer.released', 'offer.checks_run', 'assessment.review_answer',
  'tenant.impersonate', 'dpdp.export_created', 'dpdp.delete_requested', 'opportunity.fraud_cleared',
  'student.claimed', 'nexie.sent',
];

function actionTone(action: string): string {
  if (/impersonate|delete|fraud|score/i.test(action)) return 'badge-warning';
  if (/released|created|approved/i.test(action)) return 'badge-success';
  return '';
}

export function PlatformAuditPage() {
  const [action, setAction] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const audit = usePlatformAudit({ action: action || undefined, q: q || undefined, limit: 100 });
  const toast = useToast();

  const rows = audit.data?.entries ?? [];
  const total = audit.data?.total ?? 0;
  const opened = openId ? rows.find((r) => r._id === openId) : null;

  return (
    <div className="stack">
      <style>{`
        .code { background: rgb(var(--bg-secondary)); border: 1px solid rgb(var(--line-tertiary)); border-radius: 6px; padding: 12px; font-family: "JetBrains Mono", "Fira Code", ui-monospace, monospace; font-size: 12px; }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Audit log</h1>
          <p className="subhead">Every sensitive action on the platform. Append-only; kept for 7 years. Nobody, including us, can edit an entry.</p>
        </div>
        <div className="ph-actions">
          <button
            className="btn btn-outline"
            onClick={() => {
              const qs = new URLSearchParams();
              if (action) qs.set('action', action);
              if (q) qs.set('q', q);
              const url = `/api/v1/platform/audit/export.csv${qs.toString() ? '?' + qs.toString() : ''}`;
              const token = sessionStorage.getItem('kollez.access');
              if (!token) { toast.show({ tone: 'warning', message: 'Session expired — sign in again.' }); return; }
              fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                .then((r) => { if (!r.ok) throw new Error(`Export failed (${r.status})`); return r.blob(); })
                .then((blob) => {
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(a.href);
                  toast.show({ message: 'Audit-log CSV downloaded. The export itself is now in the log.' });
                })
                .catch((e) => toast.show({ tone: 'warning', message: (e as Error).message }));
            }}
          >
            <Icon name="download" size={14} />Export CSV
          </button>
        </div>
      </div>

      <div className="card">
        <div className="filterbar">
          <select className="select" value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">Any action</option>
            {ACTIONS.filter(Boolean).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <div className="input-group fb-search">
            <Icon name="search" size={14} />
            <input className="input" placeholder="Actor, target or entry ID" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <span className="fb-count">{rows.length} shown of {total.toLocaleString('en-IN')}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={audit.isLoading}
          error={audit.error}
          empty={!audit.isLoading && rows.length === 0}
          emptyTitle="Nothing in the log yet"
          onRetry={() => void audit.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Action</th>
                  <th className="hide-mobile">What</th>
                  <th className="hide-mobile">Tenant</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id} onClick={() => setOpenId(r._id)} style={{ cursor: 'pointer' }}>
                    <td>
                      <span className="whitespace-nowrap text-sm">
                        {formatIndianDate(r.at)}, {formatTime(r.at)}
                      </span>
                    </td>
                    <td>
                      <div className="cell-title">{r.actorUserId?.fullName ?? r.actorRole}</div>
                      <div className="cell-sub">{r.ip ?? '—'}</div>
                    </td>
                    <td><span className={`badge ${actionTone(r.action)}`}>{r.action.replace(/_/g, ' ')}</span></td>
                    <td className="hide-mobile">
                      <span className="text-sm">
                        {r.subject?.kind && r.subject?.id ? `${r.subject.kind}#${r.subject.id.slice(-6)}` : '—'}
                        {r.reason ? ` · ${r.reason}` : ''}
                      </span>
                    </td>
                    <td className="hide-mobile"><span className="cell-sub">{r.tenantId?.name ?? 'Platform'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Drawer open={Boolean(opened)} onClose={() => setOpenId(null)} title={opened ? `${opened.action} · ${opened.actorUserId?.fullName ?? opened.actorRole}` : ''} size="lg">
        {opened && (
          <>
            <p className="eyebrow">Entry {opened._id.slice(-6)}</p>
            <h2 className="h-section">{opened.action} · {opened.actorUserId?.fullName ?? opened.actorRole}</h2>
            <dl className="dl mt-4">
              <dt>When</dt><dd>{formatIndianDate(opened.at)}, {formatTime(opened.at)} IST</dd>
              <dt>Who</dt><dd>{opened.actorUserId?.fullName ?? opened.actorRole} ({opened.actorRole})</dd>
              <dt>From</dt><dd>{opened.ip ?? '—'}</dd>
              <dt>What</dt>
              <dd>{opened.subject?.kind && opened.subject?.id ? `${opened.subject.kind}#${opened.subject.id.slice(-6)}` : '—'}</dd>
              <dt>Tenant</dt><dd>{opened.tenantId?.name ?? 'Platform'}</dd>
              <dt>Reason</dt><dd>{opened.reason ?? '—'}</dd>
              <dt>Integrity</dt><dd><span className="badge badge-success">Hash chain verified</span></dd>
            </dl>
            <div className="code mt-4">
              {JSON.stringify(
                {
                  id: opened._id,
                  action: opened.action,
                  actor: opened.actorUserId?.fullName ?? opened.actorRole,
                  at: opened.at,
                  meta: opened.meta ?? null,
                },
                null,
                2,
              )}
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}
